import { Flex, Loader } from "@mantine/core";
import { IAzureBlobFile } from "@schemas/azureSchema";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";

const MAX_FILE_COUNT = 50;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
const MAX_TOTAL_SIZE = 400 * 1024 * 1024; // 400MB in bytes
const MIN_SIZE = 1; // 1 byte

const FileUploader: React.FC<{
  onFileUpload: (file: File) => void;
  files: IAzureBlobFile[];
  imageOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onFileUpload, files, imageOnly = false, disabled = false, loading = false }) => {
  const acceptedFileTypes = imageOnly
    ? ["image/jpeg", "image/png", "image/gif", "image/jpg"]
    : [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
        "text/plain",
        "video/mp4",
        "application/vnd.ms-powerpoint",
        "application/vnd.ms-outlook",
        "message/rfc822",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ];

  const checkFileExtension = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    return ["msg", "xlsm"].includes(extension ?? "");
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: File[]) => {
      const existingFileCount = files?.length ?? 0;
      const nextFileCount = acceptedFiles.length + existingFileCount;
      const existingTotalSize = files?.reduce((sum, file) => sum + (file.size ?? 0), 0) ?? 0;
      const acceptedFilesTotalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
      const nextTotalSize = existingTotalSize + acceptedFilesTotalSize;

      if (nextFileCount > MAX_FILE_COUNT) {
        alert(`For mange filer. Kan kun laste opp ${MAX_FILE_COUNT} filer totalt.`);
        return;
      }

      const tooLargeFile = acceptedFiles.find((file) => file.size > MAX_FILE_SIZE);
      if (tooLargeFile != null) {
        alert(`Filen "${tooLargeFile.name}" er for stor. Maks tillatt filstørrelse er 50MB.`);
        return;
      }

      if (nextTotalSize > MAX_TOTAL_SIZE) {
        alert("Filene tar for mye plass. Maks tillatt total størrelse er 400MB.");
        return;
      }

      if (acceptedFiles.length > 0) {
        acceptedFiles.forEach((file: File) => {
          const isValidType = acceptedFileTypes.includes(file.type) || checkFileExtension(file.name);
          if (isValidType) {
            onFileUpload(file);
          } else {
            alert("Ugyldig filtype.");
          }
        });
      }

      fileRejections.forEach((file) => {
        if (file.size < MIN_SIZE) {
          alert("Filen er tom.");
        }
        if (file.size > MAX_FILE_SIZE) {
          alert(`Filen "${file.name}" er for stor. Maks tillatt filstørrelse er 50MB.`);
        }
      });
    },
    [acceptedFileTypes, files, onFileUpload],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    minSize: MIN_SIZE,
    maxSize: MAX_FILE_SIZE,
  });

  return (
    <Flex
      mb="xs"
      align="center"
      justify="center"
      {...getRootProps()}
      className={`dropzone ${disabled ? "disabled" : ""}`}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <input {...getInputProps()} disabled={disabled} />
      <div>
        {loading ? (
          <Loader />
        ) : (
          `Dra og slipp filer her, eller klikk for å velge filer (Maks ${MAX_FILE_COUNT} filer / 50MB per fil / 400MB totalt)`
        )}
      </div>
    </Flex>
  );
};

export default FileUploader;
